import { getBookingGridClassName } from "@/forms/assignment-reference-bookings-form";
import { Button } from "@msuite/ui/button";
import { Divider } from "@msuite/ui/divider";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { cn } from "@msuite/ui/utils";
import type { Nullable, Tables } from "@msuite/utils/types";
import React from "react";
import { useAssignmentReferenceBookingsModalContext } from "./context";
import { AssignmentReferenceBookingsSettlement } from "./assignment-reference-bookings-settlement";

/** Props Interface */
interface AssignmentReferenceBookingsFooterProps {
	agreement: Nullable<Tables<"organizationPartnershipAgreements">>;
	onAddBooking: () => void;
}

export function AssignmentReferenceBookingsFooter({
	agreement,
	onAddBooking,
}: AssignmentReferenceBookingsFooterProps) {
	/** Context */
	const { isApproved, role, hasSavedBookings, isReviewed, hasHostColumn } =
		useAssignmentReferenceBookingsModalContext();

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	// The partner books its hours until the host approves. The host books them only on a record
	// the partner left empty — the server approves such a record on save, so there is nothing
	// left for the host to add once it holds bookings.
	const canAddBooking = !isApproved && (role === "partner" || !hasSavedBookings);
	// The booking cards' grid, inset by their margin and padding beyond the modal's and by a
	// border as wide as theirs, so the button and each total stand under the input columns.
	const columnsClassName = cn(
		getBookingGridClassName(hasHostColumn),
		"px-1.5 border-x-[0.5px] border-transparent",
	);
	// The host's totals add up its answers. The host sees them move as it answers; the partner sees
	// them once there are answers to add up — the record reviewed or approved.
	const showsHostTotals = hasHostColumn && (role === "host" || isReviewed || isApproved);

	/** Render */
	return (
		// Pinned to the bottom of the region the bookings scroll in and painted over them as they
		// pass — `z-20` clears the `z-10` a closed select's button carries.
		<div className="bottom-0 z-20 sticky bg-primary-50 dark:bg-primary-950 py-4 border-color border-t-[0.5px] v-stack modal-padding">
			{canAddBooking && (
				<React.Fragment>
					<div className={cn(columnsClassName, "-mt-2")}>
						<div className="col-start-2">
							<Button
								size="xxs"
								icon={<Icon name="Plus" />}
								onClick={onAddBooking}
							>
								{t("partners.add_booking")}
							</Button>
						</div>
					</div>
					<Divider variant="modal" />
				</React.Fragment>
			)}
			<div className={columnsClassName}>
				<div className="col-start-2">
					<AssignmentReferenceBookingsSettlement
						agreement={agreement}
						inputRole="partner"
					/>
				</div>
				{showsHostTotals && (
					<AssignmentReferenceBookingsSettlement
						agreement={agreement}
						inputRole="host"
					/>
				)}
			</div>
		</div>
	);
}
